/**
 * Serviço de Emissão de Ingressos Cortesia / Gratuitos (Valor R$ 0,00)
 * Processa a criação de ordens gratuitas, geração de ingressos/QR Codes e disparo de WhatsApp.
 */

import { supabase } from './lib/supabase';
import { getClientIpAddress } from '../utils/utils/ipUtils';
import { sendOrderNotifications } from './orderNotificationService';
import { applyCouponOnOrder } from './couponService';

export interface CreateComplimentaryOrderParams {
  event_id: string;
  batch_index: number;
  batch_name?: string;
  unit_price?: number;
  quantity: number;
  client_name: string;
  client_phone: string;
  client_email?: string;
  client_document?: string;
  client_id?: string;
  coupon_id?: string;
  coupon_code?: string;
  discount_amount?: number;
  notes?: string;
  attendees?: Array<{
    nome: string;
    whatsapp?: string;
    email?: string;
    cpf?: string;
    documento?: string;
  }>;
  send_whatsapp?: boolean;
}

/**
 * Localiza ou cadastra uma pessoa na tabela app_people para vincular ao client_id
 */
export const findOrCreatePerson = async (data: {
  nome: string;
  documento?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  clientId?: string | null;
}): Promise<string | null> => {
  try {
    const cleanDoc = data.documento?.replace(/\D/g, '') || null;
    const rawDoc = data.documento?.trim() || null;
    const cleanPhone = data.whatsapp?.replace(/\D/g, '') || null;
    const rawPhone = data.whatsapp?.trim() || null;

    let existingPerson: any = null;

    // Um cadastro com outro CPF é outra pessoa: nunca reaproveitar nem sobrescrever
    // (casais e famílias costumam dividir o mesmo WhatsApp)
    const isSamePersonDoc = (person: any): boolean => {
      const personDoc = String(person?.documento || '').replace(/\D/g, '');
      return !cleanDoc || !personDoc || personDoc === cleanDoc;
    };

    // 1. Se já veio um clientId válido, buscar o registro existente
    if (data.clientId) {
      const { data: existing } = await supabase
        .from('app_people')
        .select('*')
        .eq('id', data.clientId)
        .maybeSingle();
      if (existing?.id && isSamePersonDoc(existing)) existingPerson = existing;
    }

    // 2. Se não encontrou por ID, tentar encontrar por CPF/Documento
    if (!existingPerson && cleanDoc) {
      let { data: byDoc } = await supabase
        .from('app_people')
        .select('*')
        .or(`documento.eq.${cleanDoc},documento.eq.${rawDoc}`)
        .limit(1)
        .maybeSingle();

      if (!byDoc) {
        const { data: directDoc } = await supabase
          .from('app_people')
          .select('*')
          .eq('documento', cleanDoc)
          .limit(1)
          .maybeSingle();
        if (directDoc) byDoc = directDoc;
      }

      if (byDoc?.id) existingPerson = byDoc;
    }

    // 3. Sem CPF informado, tentar por WhatsApp / Telefone. Com CPF que não está na
    // base é uma pessoa nova: o WhatsApp pode ser de outra pessoa da mesma família
    if (!existingPerson && !cleanDoc && cleanPhone) {
      const { data: byPhone } = await supabase
        .from('app_people')
        .select('*')
        .or(`whatsapp.eq.${rawPhone},whatsapp.eq.${cleanPhone},whatsapp.eq.+55${cleanPhone},telefone.eq.${cleanPhone}`)
        .limit(1)
        .maybeSingle();

      if (byPhone?.id) existingPerson = byPhone;
    }

    // 4. Se a pessoa já existe na base, atualizar qualquer informação alterada no modal
    if (existingPerson) {
      const updatePayload: Record<string, any> = {};

      const inputName = data.nome?.trim();
      if (inputName && inputName !== existingPerson.nome && inputName !== existingPerson.name) {
        updatePayload.nome = inputName;
      }

      // Compara sem o DDI 55 para não regravar "+55(11) 99999-9999" como "11999999999"
      const withoutBrDdi = (digits: string) => (digits.length >= 12 && digits.startsWith('55') ? digits.slice(2) : digits);
      const inputPhone = rawPhone || cleanPhone;
      const existingCleanPhone = (existingPerson.whatsapp || existingPerson.telefone || '').replace(/\D/g, '');
      if (cleanPhone && withoutBrDdi(cleanPhone) !== withoutBrDdi(existingCleanPhone)) {
        updatePayload.whatsapp = inputPhone;
      }

      const inputEmail = data.email !== undefined ? data.email?.trim() || null : undefined;
      if (inputEmail !== undefined && inputEmail !== (existingPerson.email || null)) {
        updatePayload.email = inputEmail;
      }

      if (cleanDoc && cleanDoc !== existingPerson.documento) {
        updatePayload.documento = cleanDoc;
      }

      if (Object.keys(updatePayload).length > 0) {
        updatePayload.updated_at = new Date().toISOString();
        const { error: updateErr } = await supabase
          .from('app_people')
          .update(updatePayload)
          .eq('id', existingPerson.id);

        if (updateErr) {
          console.warn('Aviso ao atualizar dados do cliente em app_people:', updateErr.message);
        }
      }

      return existingPerson.id;
    }

    // 5. Se não existir, cadastrar nova pessoa em app_people
    if (data.nome?.trim()) {
      const insertPayload = {
        nome: data.nome.trim(),
        documento: cleanDoc || rawDoc || null,
        whatsapp: rawPhone || cleanPhone || null,
        email: data.email?.trim() || null,
        ativo: true,
        is_active: true,
        validated: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data: inserted, error: insertErr } = await supabase
        .from('app_people')
        .insert(insertPayload)
        .select('id')
        .maybeSingle();

      if (!insertErr && inserted?.id) {
        return inserted.id;
      }

      // Fallback simplificado caso alguma coluna gere divergência no schema
      if (insertErr) {
        console.warn('Tentativa com payload simplificado ao inserir em app_people:', insertErr.message);
        const { data: simpleInserted } = await supabase
          .from('app_people')
          .insert({
            nome: data.nome.trim(),
            documento: cleanDoc || rawDoc || null,
            whatsapp: rawPhone || cleanPhone || null,
            email: data.email?.trim() || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select('id')
          .maybeSingle();

        if (simpleInserted?.id) return simpleInserted.id;
      }
    }

    return null;
  } catch (err) {
    console.warn('Erro ao resolver pessoa em app_people:', err);
    return data.clientId || null;
  }
};

export interface ExistingCourtesy {
  id: string;
  client_name: string | null;
}

/**
 * Cortesia ainda válida emitida para o CPF no evento. Canceladas, estornadas e
 * falhas não contam, para que o CPF possa receber uma nova.
 */
export const findActiveCourtesyByDocument = async (
  eventId: string,
  document: string
): Promise<ExistingCourtesy | null> => {
  const cleanDoc = document.replace(/\D/g, '');
  if (!cleanDoc) return null;

  const { data, error } = await supabase
    .from('app_event_orders')
    .select('id, client_name')
    .eq('event_id', eventId)
    .eq('payment_method', 'cortesia')
    .eq('client_document', cleanDoc)
    .not('status', 'in', '(cancelled,refunded,failed)')
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
};

export const courtesyAlreadyIssuedMessage = (courtesy: ExistingCourtesy): string =>
  `Este CPF já recebeu uma cortesia neste evento (pedido #${String(courtesy.id).slice(0, 8).toUpperCase()}${courtesy.client_name ? ` – ${courtesy.client_name}` : ''}).`;

export interface ComplimentaryOrderResponse {
  success: boolean;
  orderId?: string;
  order?: any;
  error?: string;
}

export const createComplimentaryOrder = async (
  params: CreateComplimentaryOrderParams
): Promise<ComplimentaryOrderResponse> => {
  try {
    const clientIp = await getClientIpAddress();
    const cleanDoc = params.client_document?.replace(/\D/g, '') || null;
    const originalSubtotal = (params.unit_price || 0) * params.quantity;
    const discount = params.discount_amount !== undefined ? params.discount_amount : originalSubtotal;

    // 0. Uma cortesia por CPF em cada evento
    if (cleanDoc) {
      let existingCourtesy: ExistingCourtesy | null;
      try {
        existingCourtesy = await findActiveCourtesyByDocument(params.event_id, cleanDoc);
      } catch {
        return { success: false, error: 'Não foi possível verificar cortesias anteriores deste CPF. Tente novamente.' };
      }
      if (existingCourtesy) {
        return { success: false, error: courtesyAlreadyIssuedMessage(existingCourtesy) };
      }
    }

    // 1. Validar estoque restante do lote no evento
    const { data: eventRow } = await supabase
      .from('app_events')
      .select('price_batches, observations')
      .eq('id', params.event_id)
      .single();

    if (eventRow) {
      let batches: any[] = Array.isArray(eventRow.price_batches) ? eventRow.price_batches : [];
      if (batches.length === 0 && eventRow.observations) {
        try {
          const parsed = JSON.parse(eventRow.observations);
          if (Array.isArray(parsed.price_batches)) batches = parsed.price_batches;
        } catch {
          // Ignora erro de parse
        }
      }

      const targetBatch = batches[params.batch_index || 0];
      if (targetBatch && targetBatch.quantity && targetBatch.quantity > 0) {
        const { data: approvedOrders } = await supabase
          .from('app_event_orders')
          .select('quantity')
          .eq('event_id', params.event_id)
          .eq('batch_index', params.batch_index || 0)
          .in('status', ['approved', 'paid', 'completed']);

        const totalSold = (approvedOrders || []).reduce(
          (acc: number, curr: any) => acc + (Number(curr.quantity) || 1),
          0
        );
        const remaining = Math.max(0, targetBatch.quantity - totalSold);

        if (remaining <= 0) {
          return { success: false, error: `O lote "${targetBatch.name || 'selecionado'}" está esgotado.` };
        }

        if (params.quantity > remaining) {
          return {
            success: false,
            error: `Restam apenas ${remaining} ingressos disponíveis para o lote "${targetBatch.name}".`,
          };
        }
      }
    }

    // 1.1 Sincronizar / Obter ID da pessoa na tabela app_people
    const resolvedClientId = await findOrCreatePerson({
      nome: params.client_name,
      documento: cleanDoc || params.client_document,
      whatsapp: params.client_phone,
      email: params.client_email,
      clientId: params.client_id,
    });

    // 2. Inserir a ordem com status 'paid' (R$ 0,00 / Cortesia)
    const { data: newOrder, error: orderErr } = await supabase
      .from('app_event_orders')
      .insert({
        event_id: params.event_id,
        client_id: resolvedClientId || params.client_id || null,
        client_name: params.client_name || 'Convidado VIP',
        client_email: params.client_email || '',
        client_phone: params.client_phone || '',
        client_document: cleanDoc,
        ip_address: clientIp,
        amount_total: 0,
        currency: 'brl',
        quantity: params.quantity,
        batch_index: params.batch_index || 0,
        batch_name: params.batch_name || 'Cortesia',
        status: 'paid',
        payment_method: 'cortesia',
        convenience_fee: 0,
        convenience_fee_percentage: 0,
        coupon_id: params.coupon_id || null,
        coupon_code: params.coupon_code || null,
        discount_amount: discount,
        cancellation_reason: params.attendees && params.attendees.length > 0 ? JSON.stringify(params.attendees) : (params.notes || null),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (orderErr || !newOrder) {
      console.error('Erro ao registrar ordem cortesia:', orderErr);
      return { success: false, error: orderErr?.message || 'Erro ao registrar inscrição cortesia.' };
    }

    const orderId = newOrder.id;

    // 3. Registrar utilização definitiva do cupom caso tenha sido utilizado
    if (params.coupon_code || params.coupon_id) {
      try {
        await applyCouponOnOrder({
          couponId: params.coupon_id || '',
          code: params.coupon_code || '',
          eventId: params.event_id,
          orderId: orderId,
          batchIndex: params.batch_index || 0,
          originalAmount: originalSubtotal,
          clientName: params.client_name,
          clientDocument: cleanDoc || undefined,
          clientPhone: params.client_phone,
          clientEmail: params.client_email,
        });
      } catch (couponErr) {
        console.warn('Aviso ao registrar uso do cupom cortesia:', couponErr);
      }
    }

    // 4. Emitir ingressos e QR Codes individuais na tabela app_event_tickets
    const qty = params.quantity || 1;
    const ticketsToInsert = [];
    const attendees = params.attendees || [];

    for (let i = 0; i < qty; i++) {
      const qrHash = `VIP-${orderId.substring(0, 8)}-${i + 1}-${Date.now().toString(36).toUpperCase()}`;

      // Tentar associar pessoa/participante se fornecido
      const att = attendees[i] || {
        nome: params.client_name,
        whatsapp: params.client_phone,
        email: params.client_email,
        documento: cleanDoc,
      };

      let attendeeClientId = (att as any)?.person_id || (att as any)?.client_id;
      if (!attendeeClientId && (att.nome || att.whatsapp || att.documento)) {
        attendeeClientId = await findOrCreatePerson({
          nome: att.nome,
          documento: (att.documento || (att as any).cpf || '').replace(/\D/g, ''),
          whatsapp: att.whatsapp,
          email: att.email,
        });
      }
      if (!attendeeClientId && i === 0) {
        attendeeClientId = resolvedClientId || params.client_id || null;
      }

      ticketsToInsert.push({
        order_id: orderId,
        event_id: params.event_id,
        client_id: attendeeClientId || null,
        ticket_number: i + 1,
        qr_code_hash: qrHash,
        status: 'valid',
        created_at: new Date().toISOString(),
      });
    }

    const { error: ticketErr } = await supabase
      .from('app_event_tickets')
      .upsert(ticketsToInsert, { onConflict: 'order_id,ticket_number', ignoreDuplicates: true });
    if (ticketErr) {
      console.warn('Aviso ao emitir ingressos cortesia:', ticketErr);
    }

    // 5. Disparar notificações de confirmação (WhatsApp + E-mail)
    sendOrderNotifications({
      type: 'confirmed',
      orderId: orderId,
      orderData: newOrder,
    }).catch((notifErr) => {
      console.warn('Aviso no envio de notificações da cortesia:', notifErr);
    });

    return {
      success: true,
      orderId: orderId,
      order: newOrder,
    };
  } catch (err: any) {
    console.error('Erro ao emitir cortesia:', err);
    return { success: false, error: err.message || 'Erro inesperado ao emitir cortesia.' };
  }
};
